import { useNavigationStore } from '../../store/navigationStore';
import { usePipelineStore } from '../../store/pipelineStore';
import { useAgentStore } from '../../store/agentStore';
import { useLogicStore } from '../../store/logicStore';
import { useWorkbenchStore } from '../../store/workbenchStore';
import { useUiStore } from '../../store/uiStore';
import { authedHeaders } from '../../store/authStore';
import { clearModuleState } from './moduleState';
import { useWorkspaceStore } from './workspaceStore';
import type { LiveWorkspaceResource } from './workspaceInventory';

export function activateProject(id: string | null) {
  const workspace = useWorkspaceStore.getState();
  if (workspace.projectId === id) return;
  if (id && !workspace.manifest.projects.some(p => p.id === id)) return;
  const session = useWorkbenchStore.getState().kernelSessionId;
  if (session) {
    // Capture the old session and credentials before clearing the editor.
    // Its eventual response must not reset a new project's kernel session.
    const api = import.meta.env.VITE_KERNEL_SERVICE_URL || 'http://localhost:8026';
    void fetch(`${api}/kernel/sessions/${encodeURIComponent(session)}`, { method: 'DELETE', headers: authedHeaders() }).catch(() => {});
  }
  clearModuleState();
  const navigation = useNavigationStore.getState();
  if (navigation.currentPage.startsWith('app-') && navigation.currentPage !== 'apps-app') navigation.navigateTo('apps');
  useNavigationStore.setState({ pendingAppId: null, pendingPipeline: null });
  workspace.openProject(id);
}

export async function openWorkspaceResource(resource: LiveWorkspaceResource) {
  activateProject(resource.projectId || null);
  const navigation = useNavigationStore.getState();
  const id = resource.resourceId;
  switch (resource.kind) {
    case 'dashboard': case 'app': case 'document': case 'solution': navigation.navigateToApp(id, resource.kind); break;
    case 'pipeline': usePipelineStore.getState().selectPipeline(id); navigation.navigateTo('pipelines'); break;
    case 'function': {
      await useLogicStore.getState().fetchFunctions();
      const fn = useLogicStore.getState().functions.find(f => f.id === id);
      if (!fn) throw new Error('La función ya no está disponible. Actualiza Archivos.');
      useLogicStore.getState().selectFunction(fn); navigation.navigateTo('logic'); break;
    }
    case 'agent': {
      await useAgentStore.getState().fetchAgents();
      const agent = useAgentStore.getState().agents.find(a => a.id === id);
      if (!agent) throw new Error('El agente ya no está disponible. Actualiza Archivos.');
      useAgentStore.getState().selectAgent(agent); navigation.navigateTo('agents'); break;
    }
    case 'notebook': await useWorkbenchStore.getState().openNotebook(id); navigation.navigateTo('workbench'); break;
    case 'connector': navigation.navigateToConnectorInstance(id); break;
    case 'object_type': navigation.navigateTo('ontology'); useUiStore.getState().openObjectPanel({ type: 'objectType', id, label: resource.name }); break;
  }
}
